import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Breadcrumbs from '../../components/breadcrumbs'
import Footer from '../../components/footer'
import DarkNav from '../../components/dark-nav'
import { getCanonicalUrl } from '../../utils/canonical'
import './cliniko.css'

const ClinikoIntegration = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [openFaq, setOpenFaq] = useState(null)

  useEffect(() => {
    if (typeof AOS !== 'undefined') {
      AOS.init({ duration: 800, easing: 'ease-out', once: false, offset: 100, mirror: true })
    }
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
      if (typeof AOS !== 'undefined') AOS.refresh()
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'Cliniko' }
  ]

  const syncPoints = [
    'DataFlows polls Cliniko continuously — new bookings, cancellations and reschedules are picked up automatically, around the clock',
    'Nothing to set up on Cliniko\'s side: no webhooks, no plugins, no manual syncing — ever',
    'Multi-business and multi-branch ready: a single DataFlows account connects one Cliniko account across multiple locations, each with its own independent settings, sender number, reminder timing and message templates — roll it out branch by branch as each one is ready',
  ]

  const reminderCapabilities = [
    {
      name: 'Rolling reminders',
      what: 'Sends a set number of hours before each appointment (e.g. "24 hours before"), recalculated individually for every booking.'
    },
    {
      name: 'Fixed daily reminders',
      what: 'Sends once a day at a set clock time (e.g. 9am), covering everything due the next day. Self-healing if a scheduled run is ever missed.'
    },
    {
      name: 'Multiple reminder slots',
      what: 'Not limited to one reminder — add a second, third, or more, each with its own independent timing and wording. A common setup pairs a 48-hour reminder with a same-day one.'
    },
    {
      name: 'Per-appointment-type messages',
      what: 'Set a different reminder message for specific appointment types — Telehealth versus in-person, for example — without maintaining separate reminder systems.'
    },
    {
      name: 'Appointment-type filtering',
      what: 'Restrict reminders to only the appointment types you choose, rather than every type the clinic offers.'
    },
    {
      name: 'Duplicate-timing protection',
      what: 'Built-in detection stops you from accidentally configuring two reminders that would fire on top of each other.'
    },
  ]

  const notificationFeatures = [
    {
      name: 'Booking Confirmation',
      what: 'An instant SMS the moment a new appointment is created in Cliniko — patients get the details in writing straight away, independent of the reminder that comes later.'
    },
    {
      name: 'Cancellation Notice',
      what: 'An instant SMS the moment an appointment is cancelled in Cliniko, rather than relying on Cliniko\'s own native notification, which many clinics don\'t have configured. Only sent to patients who were already notified about the appointment, so nobody hears about a booking they never knew existed.'
    },
    {
      name: 'Reschedule Notice',
      what: 'An instant SMS the moment Cliniko\'s own reschedule tool moves an appointment, separate from and faster than the next scheduled reminder.'
    },
    {
      name: 'Post-Appointment Follow-Up',
      what: 'A follow-up SMS sent a configurable delay after each appointment ends (e.g. "How did your visit go?"), with a no-show exclusion option so patients who didn\'t attend aren\'t asked how it went.'
    },
    {
      name: 'Recall Reminders',
      what: 'Polls Cliniko\'s own recall records — patients flagged as due for a follow-up visit, such as a 6-month checkup — and sends a reminder a configurable number of days ahead. Because this counts as marketing communication under the Spam Act, a clear opt-out notice is required and included.'
    },
    {
      name: 'Invoice SMS',
      what: 'Automatically texts a patient when a new unpaid invoice is created in Cliniko, including a live payment link when the clinic\'s Cliniko plan supports online payments.'
    },
  ]

  const aiFeatures = [
    {
      name: 'Automatic reply classification',
      what: 'Recognises confirm, cancel and reschedule intent using fast keyword matching, backed by AI classification for free-text replies like "can\'t make it Tuesday" — customisable for your clinic.'
    },
    {
      name: 'Auto-confirm in Cliniko',
      what: 'Confident "confirm" replies are confirmed directly in Cliniko. No staff action needed for a clean yes.'
    },
    {
      name: 'Smart escalation',
      what: 'Cancellations, reschedule requests, low-confidence replies and genuine questions are escalated to your staff automatically by email and/or SMS — always with the patient\'s name attached.'
    },
    {
      name: 'Graceful edge-case handling',
      what: 'A reply to the booking-confirmation text (which isn\'t meant to invite one) gets a "we\'ll text you closer to the date" response instead of a false staff alert. A duplicate reply to something already resolved gets an "already taken care of" acknowledgment instead of a second alert.'
    },
    {
      name: 'Full reply audit trail',
      what: 'Every incoming reply is logged with its detected intent, confidence score and the action taken — a complete internal record, separate from the optional Cliniko note-logging described below.'
    },
  ]

  const inboxCampaignFeatures = [
    {
      name: 'Two-way Patient Inbox',
      what: 'Every SMS conversation with every patient in one threaded inbox, with the patient\'s name resolved automatically. Staff can jump in and reply manually alongside the automated AI handling above, whenever a conversation needs a human.'
    },
    {
      name: 'Patient Browser & Bulk Import',
      what: 'Search Cliniko\'s patient list live from inside DataFlows, or import every consented patient in bulk as a background job — built to handle clinics with 10,000+ patients without timing out. Save results into a contact group or carry them straight into a campaign.'
    },
    {
      name: 'Satisfaction Survey (NPS)',
      what: 'An automatic post-appointment survey link, with a dashboard showing responses over time. High scorers can optionally be prompted to leave a public review, and survey activity can be logged back into Cliniko as a note.'
    },
    {
      name: 'Manual Campaign Builder',
      what: 'Filter patients by appointment date range and outcome — attended, no-shows or all — with a fast, non-per-patient preview and background sending so the page doesn\'t hang on large lists. Ideal for reactivation and promotions.'
    },
    {
      name: 'Recurring Campaign Schedules',
      what: 'Auto-prepare a campaign on a daily or weekly schedule with a configurable lead time (e.g. "every Monday, targeting patients with appointments in the next 14 days"). Drafts sit in a review queue for a human to check and approve — recurring automation never sends unsupervised.'
    },
  ]

  const reliabilityFeatures = [
    {
      name: 'Consent-aware messaging',
      what: 'Checks each patient\'s actual Cliniko-recorded reminder/follow-up SMS consent setting — not the separate marketing opt-in field — before sending anything.'
    },
    {
      name: 'Full activity & audit trail',
      what: 'Every automated SMS sent and every patient reply received, logged in one place. Optionally log confirmations and consent back onto the Cliniko appointment as a timestamped note, and export a full two-sided (sent + replied) CSV for compliance record-keeping.'
    },
    {
      name: 'Branded Sending Number',
      what: 'Send from a registered alpha Sender ID, so patients see your clinic\'s name (one-way only), or a dedicated virtual number, which is two-way and required for any feature that needs a patient reply — reminders, waitlist claims, inbox conversations.'
    },
    {
      name: 'Link shortening & click tracking',
      what: 'Optionally shorten links in reminder messages — a Telehealth video-call link, for example — and see when patients click them. A per-clinic toggle, so it never changes the account-wide default for clinics that don\'t want it.'
    },
    {
      name: 'Fully customisable templates',
      what: 'Every message type ships with a sensible default template, and every single one can be reworded in your clinic\'s voice — with merge tags like {patient_name}, {date}, {time}, {practitioner} and {business}.'
    },
  ]

  const waitlistPoints = [
    'Staff add patients to a waitlist by appointment type, optional practitioner, and an optional cutoff date',
    'When a cancellation is detected, a capped batch of matching patients — fairest first — is texted the freed slot',
    'First to reply CLAIM wins it. The claiming logic is race-safe, so two patients replying within the same second can\'t both be given the slot',
    'Everyone else in that batch is told it\'s taken; unclaimed offers expire on a configurable window and staff are notified to fill it another way',
  ]

  const steps = [
    {
      title: 'Create a DataFlows account',
      desc: 'Sign up at sms.dataflows.com.au. Choose any plan — Cliniko integration is available on all paid plans.'
    },
    {
      title: 'Get your Cliniko API key',
      desc: <>In Cliniko, click your name in the bottom-left corner, then <strong style={{color:'rgba(255,255,255,0.85)'}}>My Info</strong>, then scroll down to <strong style={{color:'rgba(255,255,255,0.85)'}}>Manage API keys</strong> and click <strong style={{color:'rgba(255,255,255,0.85)'}}>Add API key</strong>. Copy the generated key. DataFlows reads your Cliniko region straight from the key, so there is nothing else to note down.</>
    },
    {
      title: 'Connect in DataFlows',
      desc: 'In DataFlows, click Cliniko in the sidebar and paste your API key. DataFlows validates the connection and detects your region instantly.'
    },
    {
      title: 'Configure your settings',
      desc: 'Choose which automations to enable, set your message templates, and configure your sender ID or virtual number. Each message type can be toggled independently — and if you run multiple Cliniko businesses, everything can be configured per location.'
    },
    {
      title: 'Go live',
      desc: 'That\'s it. DataFlows starts monitoring your Cliniko account and sending SMS automatically from that point forward.'
    },
  ]

  const requirements = [
    'An active DataFlows account (paid plan)',
    'A Cliniko account (any plan)',
    'A Cliniko API key (generated in Cliniko under My Info → Manage API keys)',
    'A virtual number or approved Sender ID in DataFlows (for two-way AI replies, a virtual number is required)',
  ]

  const faqItems = [
    {
      q: 'Can this replace Cliniko\'s built-in SMS reminders?',
      a: 'Yes — that\'s exactly what it\'s designed for. DataFlows covers everything Cliniko\'s native reminders do, plus instant booking confirmations, cancellation and reschedule notices, AI-handled replies, recalls, follow-ups, invoice SMS and campaigns. Most practices switch off Cliniko\'s native reminders and let DataFlows handle all patient SMS.'
    },
    {
      q: 'Does this work with all Cliniko plans?',
      a: 'Yes — DataFlows connects via the standard Cliniko API, which is available on all plans.'
    },
    {
      q: 'Do patients need to do anything to receive SMS?',
      a: 'No. DataFlows reads appointment and patient data directly from Cliniko. Patients receive SMS on the mobile number stored in their Cliniko record.'
    },
    {
      q: 'What happens when a patient replies?',
      a: 'Every reply is classified automatically as confirm, cancel, reschedule or question — using AI plus keyword triggers you can customise. Confident confirmations are confirmed directly in Cliniko with no staff action; everything else is escalated to your staff by email and/or SMS. Every conversation also appears in the two-way Patient Inbox, where staff can reply manually at any time.'
    },
    {
      q: 'We have more than one clinic location — is that supported?',
      a: 'Yes. Settings, reminders and campaigns can all be configured per business/location for practices with multiple Cliniko businesses.'
    },
    {
      q: 'Is patient data stored by DataFlows?',
      a: <>DataFlows stores only the data required to send and log SMS (patient name, phone number, appointment time). Full privacy policy: <a href="https://dataflows.com.au/privacy-policy">dataflows.com.au/privacy-policy</a></>,
      aText: 'DataFlows stores only the data required to send and log SMS (patient name, phone number, appointment time). Full privacy policy at dataflows.com.au/privacy-policy.'
    },
    {
      q: 'Does the waitlist book the appointment automatically once a patient claims it?',
      a: 'Not in this first version. When a patient claims a freed-up slot, DataFlows sends them a confirmation and alerts your staff to finalise the booking in Cliniko. That\'s a deliberate choice for this release, not a limitation we\'re hiding — it keeps a person in the loop for the actual booking step.'
    },
    {
      q: 'Is recall SMS treated as marketing under the Spam Act?',
      a: 'Yes. Recall reminders promote a return visit rather than confirming an existing booking, so they\'re treated as marketing communication and every recall message includes a clear opt-out, in line with Australian SMS marketing rules.'
    },
    {
      q: 'What if I need help?',
      a: 'Email support@dataflows.com.au and our team will get back to you.'
    },
  ]

  return (
    <div className="cliniko-container">
      <Helmet>
        <title>Cliniko SMS Integration — Setup Guide | DataFlows</title>
        <meta name="description" content="Replace Cliniko's native SMS with DataFlows — smart reminders, instant booking/cancellation/reschedule texts, AI-handled replies, a cancellation waitlist, recalls, NPS surveys and campaigns." />
        <meta name="keywords" content="Cliniko SMS, Cliniko SMS integration, Cliniko appointment reminders SMS, Cliniko patient SMS, Cliniko SMS Australia, Cliniko automated SMS, DataFlows Cliniko, Cliniko recall SMS, Cliniko waitlist SMS, Cliniko follow-up SMS, Cliniko booking confirmation SMS, Cliniko two-way SMS" />
        <meta property="og:title" content="Cliniko SMS Integration — Setup Guide | DataFlows" />
        <meta property="og:description" content="A complete replacement for Cliniko's native SMS reminders — smart reminders, instant booking/cancellation/reschedule notices, AI-handled replies, a cancellation waitlist, recalls, follow-ups, invoice SMS, NPS surveys, inbox and campaigns." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={getCanonicalUrl('/integrations/cliniko')} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "DataFlows SMS for Cliniko",
          "applicationCategory": "HealthApplication",
          "operatingSystem": "Cliniko",
          "description": "A complete replacement for Cliniko's native SMS reminders — smart appointment reminders, instant booking confirmations, cancellation and reschedule notices, AI-handled patient replies, a cancellation waitlist, recalls, follow-ups, invoice SMS, NPS surveys, a two-way patient inbox and campaigns.",
          "offers": { "@type": "Offer", "price": "35.00", "priceCurrency": "AUD" },
          "author": { "@type": "Organization", "name": "DataFlows SMS", "url": "https://dataflows.com.au" }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dataflows.com.au/" },
            { "@type": "ListItem", "position": 2, "name": "Integrations", "item": "https://dataflows.com.au/integrations" },
            { "@type": "ListItem", "position": 3, "name": "Cliniko", "item": "https://dataflows.com.au/integrations/cliniko" }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": typeof f.a === 'string' ? f.a : f.aText }
          }))
        })}</script>
      </Helmet>

      <DarkNav />

      <div className="cliniko-breadcrumbs">
        <div className="cliniko-inner">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      {/* Hero */}
      <section className="cliniko-hero" data-aos="fade-up">
        <div className="cliniko-inner">
          <div className="cliniko-hero-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
            Cliniko Integration
          </div>
          <h1 className="cliniko-hero-title">DataFlows + Cliniko</h1>
          <p className="cliniko-hero-subtitle">
            A complete, set-and-forget replacement for Cliniko's built-in SMS reminders.
            Smart reminders, instant booking confirmations, cancellation and reschedule notices, AI-handled patient
            replies, a cancellation waitlist, recalls, follow-ups, invoice SMS, NPS surveys, a two-way patient inbox
            and campaigns — all running automatically from your Cliniko data.
          </p>
          <div className="cliniko-hero-actions">
            <a
              href="https://sms.dataflows.com.au/cliniko/connect"
              target="_blank"
              rel="noreferrer noopener"
              className="cliniko-btn-primary"
            >
              Get started
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
            </a>
            <a
              href="https://www.cliniko.com/connected-apps/dataflows-sms/"
              target="_blank"
              rel="noreferrer noopener"
              className="cliniko-btn-outline"
            >
              Install on Cliniko
            </a>
          </div>
          <div className="cliniko-hero-trust">
            <span>✓ ACMA Registered Originating Telco</span>
            <span>✓ Spam Act 2003 Compliant</span>
            <span>✓ Works with all Cliniko plans</span>
            <span>✓ Multi-location support</span>
            <span>✓ No coding required</span>
          </div>
        </div>
      </section>

      {/* Core sync */}
      <section className="cliniko-section" id="features" data-aos="fade-up">
        <div className="cliniko-inner">
          <div className="cliniko-section-header">
            <span className="cliniko-label">Set it and forget it</span>
            <h2>Always in Sync with Cliniko</h2>
            <p>Connect once, and DataFlows stays in lockstep with your Cliniko account from that moment on. There is nothing for your staff to run, trigger or remember.</p>
          </div>
          <div className="cliniko-requirements">
            {syncPoints.map((r, i) => (
              <div key={i} className="cliniko-requirement">
                <span className="cliniko-requirement-icon">✓</span>
                <span>{r}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Smart appointment reminders */}
      <section className="cliniko-section cliniko-section--dark" data-aos="fade-up">
        <div className="cliniko-inner">
          <div className="cliniko-section-header">
            <span className="cliniko-label">Smart appointment reminders</span>
            <h2>Reminders That Fit How Your Clinic Actually Runs</h2>
            <p>Not just one reminder at one fixed time. Mix timing modes, stack multiple reminders, and tailor wording by appointment type — all without juggling separate systems.</p>
          </div>
          <div className="cliniko-capability-grid">
            {reminderCapabilities.map((f, i) => (
              <div key={i} className="cliniko-capability" data-aos="fade-up" data-aos-delay={i * 60}>
                <h3>{f.name}</h3>
                <p>{f.what}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Automatic notifications */}
      <section className="cliniko-section" data-aos="fade-up">
        <div className="cliniko-inner">
          <div className="cliniko-section-header">
            <span className="cliniko-label">Automatic notifications</span>
            <h2>Every Patient SMS, On Autopilot</h2>
            <p>
              Each message type below is independently toggleable and has its own editable template.
              And unlike Cliniko's built-in reminders, DataFlows also texts patients the <em>instant</em> a
              booking is created, cancelled or moved — not just before the appointment.
            </p>
          </div>
          <table className="cliniko-feature-table">
            <thead>
              <tr>
                <th>Message type</th>
                <th>What happens</th>
              </tr>
            </thead>
            <tbody>
              {notificationFeatures.map((f, i) => (
                <tr key={i}>
                  <td>{f.name}</td>
                  <td>{f.what}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* AI replies */}
      <section className="cliniko-section cliniko-section--dark" data-aos="fade-up">
        <div className="cliniko-inner">
          <div className="cliniko-section-header">
            <span className="cliniko-label">Two-way, handled by AI</span>
            <h2>Patient Replies That Handle Themselves</h2>
            <p>When patients text back, DataFlows understands the reply and acts on it — confirmations flow straight into Cliniko, and only the messages that genuinely need a human reach your staff.</p>
          </div>
          <table className="cliniko-feature-table">
            <thead>
              <tr>
                <th>Capability</th>
                <th>What happens</th>
              </tr>
            </thead>
            <tbody>
              {aiFeatures.map((f, i) => (
                <tr key={i}>
                  <td>{f.name}</td>
                  <td>{f.what}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Waitlist */}
      <section className="cliniko-section" data-aos="fade-up">
        <div className="cliniko-inner">
          <div className="cliniko-section-header">
            <span className="cliniko-label">Fill cancellations automatically</span>
            <h2>Cliniko Waitlist SMS</h2>
            <p>When a slot frees up, DataFlows can offer it straight to waiting patients instead of leaving reception to work the phones.</p>
          </div>
          <div className="cliniko-requirements">
            {waitlistPoints.map((r, i) => (
              <div key={i} className="cliniko-requirement">
                <span className="cliniko-requirement-icon">✓</span>
                <span>{r}</span>
              </div>
            ))}
          </div>
          <div className="cliniko-callout">
            <strong>Booking is still finalised by staff.</strong> In this first version, DataFlows doesn't create the Cliniko appointment automatically — a winning patient gets a confirmation text, and staff get an alert to complete the booking in Cliniko. That's a deliberate choice for this release, not a limitation we're hiding.
          </div>
          <p className="cliniko-section-link">
            Read the full walkthrough: <Link to="/blog/cliniko-waitlist-fill-sms" className="cliniko-inline-link">Fill Last-Minute Cancellations with Cliniko SMS Waitlists</Link>
          </p>
        </div>
      </section>

      {/* Feedback, data & campaigns */}
      <section className="cliniko-section cliniko-section--dark" data-aos="fade-up">
        <div className="cliniko-inner">
          <div className="cliniko-section-header">
            <span className="cliniko-label">Feedback, data &amp; campaigns</span>
            <h2>Talk to Patients, and Win Them Back</h2>
            <p>Beyond automated notifications, DataFlows gives your front desk a full conversation view, a way to gauge patient satisfaction, and marketing tools built around your real appointment data.</p>
          </div>
          <table className="cliniko-feature-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>What happens</th>
              </tr>
            </thead>
            <tbody>
              {inboxCampaignFeatures.map((f, i) => (
                <tr key={i}>
                  <td>{f.name}</td>
                  <td>{f.what}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Reliability, branding & compliance */}
      <section className="cliniko-section" data-aos="fade-up">
        <div className="cliniko-inner">
          <div className="cliniko-section-header">
            <span className="cliniko-label">Reliability, branding &amp; compliance</span>
            <h2>Built for Trust and Record-Keeping</h2>
            <p>Everything is logged, exportable and customisable, patient consent is respected by default, and every message sends from a number your patients recognise.</p>
          </div>
          <table className="cliniko-feature-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>What happens</th>
              </tr>
            </thead>
            <tbody>
              {reliabilityFeatures.map((f, i) => (
                <tr key={i}>
                  <td>{f.name}</td>
                  <td>{f.what}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* How to connect */}
      <section className="cliniko-section cliniko-section--dark" id="setup" data-aos="fade-up">
        <div className="cliniko-inner">
          <div className="cliniko-section-header">
            <span className="cliniko-label">Setup</span>
            <h2>How to Connect</h2>
            <p>Five steps from sign-up to live SMS — no developers needed.</p>
          </div>
          <div className="cliniko-steps">
            {steps.map((s, i) => (
              <div key={i} className="cliniko-step" data-aos="fade-up" data-aos-delay={i * 60}>
                <div className="cliniko-step-num">{i + 1}</div>
                <div className="cliniko-step-content">
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="cliniko-section" id="requirements" data-aos="fade-up">
        <div className="cliniko-inner">
          <div className="cliniko-section-header">
            <span className="cliniko-label">Requirements</span>
            <h2>What You Need</h2>
          </div>
          <div className="cliniko-requirements">
            {requirements.map((r, i) => (
              <div key={i} className="cliniko-requirement">
                <span className="cliniko-requirement-icon">✓</span>
                <span>{r}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="cliniko-section cliniko-section--dark" id="faq" data-aos="fade-up">
        <div className="cliniko-inner">
          <div className="cliniko-section-header">
            <span className="cliniko-label">FAQ</span>
            <h2>Common Questions</h2>
          </div>
          <div className="cliniko-faq">
            {faqItems.map((item, i) => (
              <div key={i} className={`cliniko-faq-item${openFaq === i ? ' open' : ''}`}>
                <button className="cliniko-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {item.q}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                </button>
                <p className={`cliniko-faq-a${openFaq === i ? '' : ' cliniko-faq-a--hidden'}`}>{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="cliniko-section" data-aos="fade-up">
        <div className="cliniko-inner">
          <h2 className="cliniko-related-title">Related Integrations</h2>
          <div className="cliniko-related-links">
            <Link to="/integrations/zapier" className="cliniko-related-link">Zapier SMS</Link>
            <Link to="/integrations/power-automate" className="cliniko-related-link">Power Automate SMS</Link>
            <Link to="/integrations/shopify" className="cliniko-related-link">Shopify SMS</Link>
            <Link to="/integrations/gohighlevel" className="cliniko-related-link">GoHighLevel SMS</Link>
            <Link to="/integrations/email-to-sms" className="cliniko-related-link">Email to SMS</Link>
            <span className="cliniko-related-link">Cliniko SMS Australia</span>
            <span className="cliniko-related-link">Healthcare SMS Automation</span>
            <span className="cliniko-related-link">Patient Appointment Reminders SMS</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cliniko-cta" data-aos="fade-up">
        <div className="cliniko-inner">
          <h2>Ready to Connect?</h2>
          <p>Connect DataFlows to Cliniko and start automating patient SMS in minutes.</p>
          <div className="cliniko-cta-buttons">
            <a
              href="https://sms.dataflows.com.au/cliniko/connect"
              target="_blank"
              rel="noreferrer noopener"
              className="cliniko-btn-primary"
            >
              Get started
            </a>
            <a
              href="https://www.cliniko.com/connected-apps/dataflows-sms/"
              target="_blank"
              rel="noreferrer noopener"
              className="cliniko-btn-outline"
            >
              Install on Cliniko
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {showScrollTop && (
        <div className="cliniko-scroll-top show" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <svg viewBox="0 0 24 24" fill="none"><path d="M12 5L19 12H16V19H8V12H5L12 5Z" fill="currentColor"/></svg>
        </div>
      )}
    </div>
  )
}

export default ClinikoIntegration
